import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { DrawerProps, Drawer } from 'components/ui';
import { FeedbackForm } from './FeedbackForm';
import { useEventFeedbackData, useFeedbackForm, useFeedbackSubmission } from './hooks';

export interface EventFeedbackDrawerProps
  extends Omit<DrawerProps, 'title' | 'children'> {
  eventId: string;
}

export const EventFeedbackDrawer = ({
  eventId,
  ...props
}: EventFeedbackDrawerProps): ReactElement => {
  const { event, feedbackStatus, existingFeedback, questions } =
    useEventFeedbackData(eventId, props.open || false);

  const {
    control,
    handleSubmit,
    errors,
    isSubmitting,
    setValue,
    selectedQuestionIds,
    rating,
  } = useFeedbackForm({
    existingFeedback: existingFeedback || null,
    isOpen: props.open || false,
  });

  const handleCloseCallback = props.onClose || (() => {
    // No-op
  });

  const { onSubmit, handleClose } = useFeedbackSubmission(
    eventId,
    feedbackStatus?.hasSubmitted || false,
    handleCloseCallback,
  );

  if (!event) {
    return (
      <Drawer
        title=""
        width={600}
        padding={24}
        {...props}
        onClose={handleClose}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
        </Box>
      </Drawer>
    );
  }

  const isReadOnly = feedbackStatus?.hasSubmitted || false;

  return (
    <Drawer
      title="How was the event?"
      width={600}
      padding={24}
      {...props}
      onClose={handleClose}
    >
      <Typography
        sx={{
          fontSize: pxToEm(24),
          fontWeight: 600,
          mb: 8,
          color: '#383838',
        }}
      >
        {event.name}
      </Typography>

      <FeedbackForm
        questions={questions}
        selectedQuestionIds={selectedQuestionIds}
        rating={rating}
        control={control}
        setValue={setValue}
        handleSubmit={handleSubmit}
        errors={errors}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
        readOnly={isReadOnly}
      />
    </Drawer>
  );
};
